import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';

/**
 * FE posting-status projection (86d3mnuxe) — mirrors CSC_CORE `OutboxStatusDto`. The posting lifecycle
 * moved off the SO/CL/IR/MIS entities onto the outbox, so the FE reads status from here: MIS/IR poll it,
 * SO/CL refetch it on an SSE nudge. Only in-flight (`Posting`) + dead-lettered (`Failed`) jobs are
 * projected; a settled post is gone from this list and shows via the entity's NetSuite id instead.
 */
export interface OutboxPostingStatusDto {
  id: number;
  type: string;
  orderingKey: string | null;
  /** Sub-identity for multi-row flows — e.g. the MIS batch: `picklist:{id}:batch:{batchId}`. */
  correlationId: string | null;
  /** Collapsed for the FE: "Posting" (Pending/Claimed — in flight) or "Failed". */
  status: 'Posting' | 'Failed';
  /**
   * PW-02 — the raw job status. `status` above collapses Pending and Claimed into one word, so on its own
   * it cannot tell "a dispatcher is running this right now" from "queued, never tried" from "failed and
   * backing off". All three read as "Posting".
   */
  statusCode: OutboxJobStatus;
  lastError: string | null;
  /** Structured detail (e.g. SO/CL pricing-drift) the drift dialog reads. */
  failureDetailJson: string | null;
  attempts: number;
  /** PW-02 — the attempt budget, so "attempt N of M" copy comes from live config and never hard-coded
   * numbers: the budget is tunable, and copy that outlives the knob is a lie. */
  maxAttempts: number;
  createdAt: string;
  /**
   * PW-02 — the batch id, parsed server-side out of `correlationId`. Null for every flow whose correlation
   * is not a picklist batch (SO/CL/IR correlate one job per entity).
   */
  postingBatchId: number | null;
  /**
   * PW-02 — when the dispatcher will next pick this job up. The only way to tell a job backing off, or
   * waiting its turn behind NetSuite's concurrency gate, from one that is queued and ready: all are
   * Pending, and a defer leaves `lastError` null because a defer is not a failure.
   */
  nextAttemptAt: string;
  /** PW-02 — when a dispatcher actually started the job, as distinct from when the user pressed Post
   * (`createdAt`). Null until first claimed. */
  claimedAt: string | null;
  /**
   * PW-02 — on a Failed row: a deterministic refusal (`true`, retrying cannot change it) or an exhausted
   * attempt budget (`false`). Null on any row that is not Failed.
   *
   * ⚠️ This says nothing about whether NetSuite was reached — a 4xx AFTER the record was created is both
   * permanent and reached. Copy claiming "nothing was issued" must also check `postReach`.
   */
  isPermanentFailure: boolean | null;
  /** 86d3mnuxe FIX 3 — how far failed attempts got toward NetSuite (see PostReach). Sticky/monotonic. */
  postReach: PostReach;
  /** The NetSuite internal id of the record a failed attempt created, when known. */
  netSuiteRecordId: string | null;
  /**
   * 86d3w2jrj BE-7 — every execution of this job, oldest first. Served ONLY on dead-letter reads
   * (a Failed job is being diagnosed; the in-flight polls stay lean), so it is `[]` everywhere else.
   */
  timeline?: OutboxAttemptDto[];
}

/**
 * The subset of a job the dead-letter diagnosis needs. Kept structural so the detail component can be
 * fed by any caller holding a status projection, without importing the health feature's aliases.
 */
export type DeadLetterJobLike = Pick<
  OutboxPostingStatusDto,
  'postReach' | 'netSuiteRecordId' | 'failureDetailJson' | 'timeline'
>;

/** 86d3w2jrj BE-7 — one execution of an outbox job. Mirrors CSC_CORE `OutboxAttemptDto`. */
export interface OutboxAttemptDto {
  /** The job's attempt counter at claim (1-based). REPEATS across Deferred rows, which don't advance it. */
  attempt: number;
  startedAt: string;
  durationMs: number | null;
  outcome: AttemptOutcome;
  /** Null on success. On a defer, the reason — a queue diagnosis rather than an error. */
  errorMessage: string | null;
}

/** Mirrors the backend `OutboxAttemptOutcomeEnum` (serialized as a number). */
export const AttemptOutcome = {
  Succeeded: 0,
  FailedTransient: 1,
  FailedPermanent: 2,
  /** Gate contention or upstream throttle — NOT a failure, and it does not burn an attempt. */
  Deferred: 3,
} as const;
export type AttemptOutcome = (typeof AttemptOutcome)[keyof typeof AttemptOutcome];

/** PW-02 — mirrors the backend `OutboxJobStatusEnum` (serialized as a number). Only Pending, Claimed and
 * Failed ever reach the FE: the projections return non-terminal and dead-lettered jobs only. */
export const OutboxJobStatus = {
  Pending: 0,
  Claimed: 1,
  Done: 2,
  Failed: 3,
  Cancelled: 4,
} as const;
export type OutboxJobStatus = (typeof OutboxJobStatus)[keyof typeof OutboxJobStatus];

/** 86d3mnuxe FIX 3 — mirrors the backend PostReachEnum (serialized as a number). */
export const PostReach = {
  Unknown: 0,
  NotReached: 1,
  Ambiguous: 2,
  Reached: 3,
} as const;
export type PostReach = (typeof PostReach)[keyof typeof PostReach];

/**
 * A record MAY (Ambiguous) or DOES (Reached) exist in NetSuite for this failed job — re-posting or
 * abandoning it is unsafe; the only safe action is a retry, which adopts + reconciles the record.
 */
export function mayHaveReachedNetSuite(job: OutboxPostingStatusDto): boolean {
  return job.postReach >= PostReach.Ambiguous;
}

/**
 * 86d3mnuxe FIX 6 — mirrors the backend mutation guard (`EnsureNotPostingAsync` +
 * `EnsureNoReachedFailedPostAsync`): the entity is locked while a post is in flight, AND after a
 * failure that may have reached NetSuite (editing then would diverge from the record a retry adopts).
 * The BE rejects edits in both states, so the FE goes read-only rather than offering a save that will
 * 400. A NotReached failure does NOT lock — nothing posted, editing is the fix.
 */
export function postingLocksEntity(job: OutboxPostingStatusDto | null): boolean {
  return job != null && (job.status === 'Posting' || mayHaveReachedNetSuite(job));
}

/**
 * 86d3mnuxe funnel — the SUBMIT gate (broader than the edit lock above): a fresh submit is blocked
 * while ANY job is in flight OR Failed. Every posting intent ends in exactly Done or Cancelled — a
 * Failed one is resolved from the posting card (Retry/Cancel when it never reached NetSuite;
 * Reconcile when it may have) before the document accepts a new intent. The BE claim rejects this
 * case too; the FE disables the button so users are guided instead of 400'd. Editing is governed by
 * `postingLocksEntity` — a NotReached failure stays editable while its Submit is blocked.
 */
export function postingBlocksSubmit(job: OutboxPostingStatusDto | null): boolean {
  return job != null && (job.status === 'Posting' || job.status === 'Failed');
}

/** Tooltip for a submit disabled by `postingBlocksSubmit` — routes the user to the posting card. */
export function submitBlockedReason(job: OutboxPostingStatusDto | null): string | undefined {
  if (job == null) return undefined;
  if (job.status === 'Posting') return 'Posting to NetSuite is in progress.';
  if (job.status !== 'Failed') return undefined;
  return mayHaveReachedNetSuite(job)
    ? 'The last posting failed but may have reached NetSuite — reconcile it from the posting card first.'
    : 'The last posting failed — retry or cancel it from the posting card first.';
}

/**
 * 86d3mnuxe FIX 4 (Cancel half) — close a Failed job WITHOUT re-running it (`Failed → Cancelled`).
 * Admin-only (`system.manage_outbox`); the BE refuses (400) when the post may have reached NetSuite
 * (cancelling would abandon the record — retry/reconcile is the only safe close), so callers only
 * offer it when `!mayHaveReachedNetSuite(job)`. Throws on refusal with the server's reason.
 */
export async function cancelOutboxJob(jobId: number): Promise<void> {
  const { data } = await apiClient.post<ApiResponse<object>>(`/admin/outbox/cancel/${jobId}`);
  if (!data.success) throw new Error(data.message || 'Cancel failed');
}

/**
 * 86d3mnuxe — re-arm a dead-lettered (`Failed`) job: reset attempts + flip to Pending, then the dispatcher
 * retries it (handlers are idempotent). Admin-only (`system.manage_outbox`). This is the System Health
 * dead-letter table's "Requeue" action. Throws on failure with the server's reason.
 */
export async function replayOutboxJob(jobId: number): Promise<void> {
  const { data } = await apiClient.post<ApiResponse<object>>(`/admin/outbox/replay/${jobId}`);
  if (!data.success) throw new Error(data.message || 'Requeue failed');
}

/** OrderingKey builders — MUST match the backend enqueue sites (86d3mnuxe). */
export const outboxKey = {
  salesOrder: (id: number): string => `salesorder:${id}`,
  quotation: (id: number): string => `quotation:${id}`,
  receiving: (id: number): string => `receiving:${id}`,
  picklist: (id: number): string => `picklist:${id}`,
} as const;

/** OutboxJob.Type keys — MUST match CSC_CORE `OutboxJobTypes` (86d3mnuxe). Used by the bulk list overlay. */
export const outboxType = {
  salesOrder: 'posting.so',
  quotation: 'posting.cl',
  receiving: 'posting.ir',
  picklist: 'posting.mis',
} as const;

/**
 * 86d3peur0 #12 — a local stand-in for the job a successful claim just created, covering the gap between the
 * 202 and the SSE nudge that refetches the real projection.
 *
 * This is an echo, not a guess: the claim already committed the job server-side before returning, so "a post
 * is in flight" is known true at this point. Without it the panel keeps its pre-click state — Submit still
 * enabled, no posting card, fields still editable — for a whole SSE → refetch round-trip, which is precisely
 * the "never put a fetch between the click and the first visible change" rule in CLAUDE.md's responsiveness
 * gate. The first refetch replaces it with the server's row.
 *
 * `id: -1` marks it local-only (server ids are positive); nothing keys off the id before that refetch.
 */
export function optimisticPostingJob(orderingKey: string, type: string): OutboxPostingStatusDto {
  return {
    id: -1,
    type,
    orderingKey,
    // SO/CL correlate one job per entity, so this mirrors the backend's enqueue (OutboxKeys).
    correlationId: orderingKey,
    status: 'Posting',
    statusCode: OutboxJobStatus.Pending,
    lastError: null,
    failureDetailJson: null,
    attempts: 0,
    // PW-02 — the budget is a server-side config value this echo cannot know. It is only ever read to
    // render "attempt N of M", which no surface shows for a row that has not attempted anything yet; the
    // first refetch brings the real number.
    maxAttempts: 0,
    createdAt: new Date().toISOString(),
    // No batch identity: the flows that use this echo correlate one job per entity, so there is nothing to
    // parse — the same answer the server's parser gives for this correlation shape.
    postingBatchId: null,
    nextAttemptAt: new Date().toISOString(),
    claimedAt: null,
    isPermanentFailure: null,
    postReach: PostReach.Unknown,
    netSuiteRecordId: null,
  };
}

/** Parse the entity id from an OrderingKey (`receiving:45` → 45, `picklist:123` → 123). Null if unparseable. */
export function outboxDocId(orderingKey: string | null): number | null {
  const m = orderingKey?.match(/:(\d+)$/);
  return m ? Number(m[1]) : null;
}

/**
 * The in-flight + failed outbox jobs for an entity's OrderingKey. Empty means nothing is posting.
 * MIS is multi-row (many partial posts per picklist share `picklist:{id}`); SO/CL/IR are single-row.
 */
export async function fetchOutboxStatus(orderingKey: string): Promise<OutboxPostingStatusDto[]> {
  const { data } = await apiClient.get<ApiResponse<OutboxPostingStatusDto[]>>('/outbox/status', {
    params: { orderingKey },
  });
  // 86d3mnuxe BUG 4 — a 200 {success:false} must THROW, not collapse to []. The settle-polls treat []
  // as "job settled" and stop permanently; throwing routes the envelope failure into their existing
  // catch/continue transient handling, same as a thrown network error.
  if (!data.success) throw new Error(data.message || 'Outbox status fetch failed');
  return data.data ?? [];
}

/**
 * All in-flight + failed jobs of a posting `type` (from `outboxType`) — one call to overlay the posting
 * badge on every row of a list + drive its cross-session auto-refresh, without a per-row fetch (86d3mnuxe).
 */
export async function fetchActiveOutboxStatuses(type: string): Promise<OutboxPostingStatusDto[]> {
  const { data } = await apiClient.get<ApiResponse<OutboxPostingStatusDto[]>>('/outbox/active', {
    params: { type },
  });
  // Deliberately NOT throwing here (unlike fetchOutboxStatus): this is the bulk list-badge overlay,
  // called inline from list fetches — an envelope failure just means "no badges this cycle" and the
  // cross-session auto-refresh self-heals it. Throwing would break the whole list load instead.
  return data.success && data.data ? data.data : [];
}

/** Group active jobs by entity id (parsed from OrderingKey) — for overlaying list rows. */
export function groupOutboxByDocId(
  jobs: OutboxPostingStatusDto[],
): Map<number, OutboxPostingStatusDto[]> {
  const map = new Map<number, OutboxPostingStatusDto[]>();
  for (const job of jobs) {
    const id = outboxDocId(job.orderingKey);
    if (id === null) continue;
    const arr = map.get(id);
    if (arr) arr.push(job);
    else map.set(id, [job]);
  }
  return map;
}
